import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EstimateApi from './estimateApi';
import { ItemizedEstimate, EstimateTotal } from './estimateType';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';
import { formatCurrency } from '../lib/global_function';

interface ItemizedEstimatesViewProps {
    estimateId: string;
}

const ItemizedEstimateView: React.FC<ItemizedEstimatesViewProps> = ({ estimateId }) => {
    const [itemizedEstimates, setItemizedEstimates] = useState<ItemizedEstimate[]>([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const navigate = useNavigate();
    const [estimateTotals, setEstimateTotals] = useState<EstimateTotal>({
        total_dollar: 0,
        total_hour: 0,
        non_optional_dollar: 0,
        non_optional_hour: 0,
        optional_dollar: 0,
        optional_hour: 0,
        discount_usd: 0,
        tax_rate: 0,
        tax_amount: 0,
        final_total: 0,
    });

useEffect(() => {
  const fetchItemizedEstimates = async () => {
    try {
      setLoading(true);
      const result = await EstimateApi.getItemizedEstimates(estimateId);

      // Safely extract items from different possible shapes
      let items: ItemizedEstimate[] = [];

      if ('itemizedEstimates' in result && Array.isArray(result.itemizedEstimates)) {
        items = result.itemizedEstimates;
      } else if ('data' in result) {
        // result.data might be { data: [...] } or an array itself
        const maybeData = (result as any).data;
        if (Array.isArray(maybeData)) {
          items = maybeData;
        } else if (maybeData?.data && Array.isArray(maybeData.data)) {
          items = maybeData.data;
        } else {
          items = [];
        }
      } else {
        items = [];
      }

      setItemizedEstimates(items);

      // Merge totals into previous state so missing fields keep defaults
      if ('totals' in result && result.totals) {
        setEstimateTotals(prev => ({
          ...prev,
          ...result.totals,
        }));
      }

    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  fetchItemizedEstimates();
}, [estimateId]);


    const handleChange = () => {
        if (estimateId) {
            navigate('/main/project/estimate/itemized_estimate/change', { state: { estimate_id: estimateId } });
        } else {
            setErrorMessage("Error: update estimate");
        }
    };

    const handleDelete = async (ItemizedestimateId: string) => {
        const selectedItemizedEstimate = itemizedEstimates.find(itemizedEstimates => itemizedEstimates.itemized_estimate_id === ItemizedestimateId);
    };

    // Convert string/number values to numbers for calculations
    const subtotal = Number(estimateTotals.total_dollar || 0);
    const nonOptionalAmount = Number(estimateTotals.non_optional_dollar || 0);
    const optionalAmount = Number(estimateTotals.optional_dollar || 0);
    const discountAmount = Number(estimateTotals.discount_usd || 0);
    const taxAmount = Number(estimateTotals.tax_amount || 0);
    const taxRate = Number(estimateTotals.tax_rate || 0);
    const finalTotal = Number(estimateTotals.final_total || 0);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <Spinner size="md" fullScreen={false} />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Error Message */}
            {errorMessage && (
                <Alert variant="error">
                  {errorMessage}
                </Alert>
            )}

            {/* Success Message */}
            {message && (
                <Alert variant="success">
                  {message}
                </Alert>
            )}

            {/* Header */}
            <div className="ml-4">
                <span className="font-semibold text-foreground">Itemized Estimates:</span>
            </div>

            {/* No Items Found */}
            {itemizedEstimates.length <= 0 && (
                <div className="ml-12 text-sm text-gray-600">
                    Not found
                </div>
            )}

            {/* Itemized Estimates List */}
            {itemizedEstimates.map((item, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    {/* Code */}
                    <div className="mb-2">
                        <span className="font-semibold text-foreground">Code:</span>
                        <span className="ml-8 text-sm text-gray-700">{item.code}</span>
                    </div>

                    {/* Description */}
                    <div className="flex mb-2">
                        <span className="font-semibold text-foreground min-w-[95px]">Description:</span>
                        <div className="ml-8 text-sm text-gray-700 flex-1 break-words whitespace-pre-wrap">
                            {item.description}
                        </div>
                    </div>

                    {/* Details Row */}
                    <div className="text-sm flex flex-wrap gap-x-4 gap-y-1">
                        <div>
                            <span className="font-semibold text-foreground">Unit Price:</span>
                            <span className="ml-2 text-gray-700">${formatCurrency(Number(item.unit_price_dollar).toFixed(2))}</span>
                        </div>

                        <div>
                            <span className="font-semibold text-foreground">Quantity:</span>
                            <span className="ml-2 text-gray-700">{item.quantity}</span>
                        </div>

                        <div>
                            <span className="font-semibold text-foreground">Sub Total:</span>
                            <span className="ml-2 text-gray-700">${formatCurrency(Number(item.sub_total_dollar).toFixed(2))}</span>
                        </div>

                        <div>
                            <span className="font-semibold text-foreground">Item is:</span>
                            <span className="ml-2 text-gray-700">{item.is_optional ? 'Optional' : 'Non-optional'}</span>
                        </div>
                    </div>
                </div>
            ))}

            {/* Totals Summary */}
            {itemizedEstimates.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-br from-primary/5 to-primary/10  border-2 border-primary/30 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-foreground mb-3 text-base">Estimate Summary</h4>
                    <div className="space-y-2 text-sm">
                        {/* Non-optional Items */}
                        {nonOptionalAmount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">Non-optional Items:</span>
                                <span className="font-semibold text-foreground">
                                    ${formatCurrency(nonOptionalAmount.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Optional Items */}
                        {optionalAmount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">Optional Items:</span>
                                <span className="font-semibold text-foreground">
                                    ${formatCurrency(optionalAmount.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Subtotal - only show if there are both optional and non-optional */}
                        {nonOptionalAmount > 0 && optionalAmount > 0 && (
                            <div className="flex justify-between pt-2 border-t border-gray-300">
                                <span className="font-semibold text-foreground">Subtotal:</span>
                                <span className="font-semibold text-foreground">
                                    ${formatCurrency(subtotal.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Discount */}
                        {discountAmount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">Discount:</span>
                                <span className="font-semibold text-red-600">
                                    -${formatCurrency(discountAmount.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Tax */}
                        {taxAmount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">
                                    Tax {taxRate > 0 ? `(${taxRate.toFixed(2)}%)` : ''}:
                                </span>
                                <span className="font-semibold text-foreground">
                                    ${formatCurrency(taxAmount.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Final Total */}
                        <div className="flex justify-between pt-3 border-t-2 border-gray-400">
                            <span className="font-bold text-foreground text-base">Total:</span>
                            <span className="font-bold text-lg text-primary">
                                ${formatCurrency(finalTotal.toFixed(2))}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Action buttons at the bottom - Commented out in original */}
            {/* 
            {itemizedEstimates.length > 0 && (
                <div className="flex justify-end">
                    <button onClick={() => handleChange()} className="flex items-center gap-2">
                        <Edit size={16} /> Change Itemized Estimate
                    </button>
                </div>
            )}
            */}
        </div>
    );
};

export default ItemizedEstimateView;